import { parseClassicPath, stripQueryAndHash } from "./classic-path"

/**
 * Which console a URL belongs to, independent of the URL itself.
 *
 * PostHog, GA4, FullStory and Sentry all key on the raw URL, so a page changes
 * identity there whenever its URL shape changes. Sending this as a super
 * property on every event gives those dashboards a stable dimension that
 * survives URL changes.
 */
export type ConsoleSurface = "classic" | "dashboard" | "other"

/** The classic organization pages: `/organizations/{org}`, `/organizations/new`. */
const CLASSIC_ORGANIZATION_PATTERN = /^\/organizations\/[^/]+(?:\/.*)?$/

/** The App Router dashboard, mounted at `/dashboard`. */
const DASHBOARD_PATTERN = /^\/dashboard(?:\/.*)?$/

/**
 * Classify a pathname. Pure: no React, no Next, no `window` — so redirects,
 * tests and both routers can all ask the same question.
 *
 * Anything with a query string or fragment attached is normalized first, so
 * `router.asPath` and `window.location.href`'s path both work.
 */
export function classifyConsoleSurface(pathname: string): ConsoleSurface {
  const path = stripQueryAndHash(pathname)

  // Both classic workspace shapes: org-less, and the org-scoped one that old
  // links still carry.
  if (parseClassicPath(path) != null) return "classic"

  // `/organizations/{org}` and `/organizations/new` are classic pages too.
  if (CLASSIC_ORGANIZATION_PATTERN.test(path)) return "classic"

  if (DASHBOARD_PATTERN.test(path)) return "dashboard"

  return "other"
}

/**
 * The classic page's section — the first path token below the workspace root
 * (`devices`, `developer`, `settings`, `customers`, `acs`, …).
 *
 * `null` whenever there is no section to report: outside a classic workspace
 * URL, and at the workspace root itself, which has no page of its own (it
 * redirects). Callers unregister the property rather than sending `null`.
 */
export function getClassicSection(pathname: string): string | null {
  const parsed = parseClassicPath(pathname)
  if (parsed == null) return null

  const section = parsed.subpath.split("/")[1]
  if (section == null || section === "") return null

  return section
}

export interface ConsoleSurfaceProperties {
  console_surface: ConsoleSurface
  classic_section: string | null
}

/**
 * The analytics properties for a pathname, as one object.
 *
 * The single place the two properties are computed together, so the Pages
 * Router, the App Router dashboard and any server-side caller can't drift.
 */
export function getConsoleSurfaceProperties(
  pathname: string
): ConsoleSurfaceProperties {
  return {
    console_surface: classifyConsoleSurface(pathname),
    classic_section: getClassicSection(pathname),
  }
}
